import type { AdvisorAccount } from "@/utils/accounting";
import type { AdvisorMovementKind } from "@/types";

// Exportación a Excel de la cuenta corriente de cada asesor
// (cuánto compró, cuánto tiene en consignación y cuánto debe).

const KIND_LABEL: Record<AdvisorMovementKind, string> = {
  consignacion: "Consignación",
  compra: "Compra del asesor",
  venta: "Venta a cliente",
  pago: "Abono / pago",
  fee: "Fee logístico",
  devolucion: "Devolución",
};

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const shortDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("es-CO");
};

const advisorName = (a: AdvisorAccount): string =>
  `${a.advisor.names} ${a.advisor.surnames}`.trim();

const summaryRows = (a: AdvisorAccount): (string | number)[][] => [
  ["Asesor", advisorName(a)],
  ["Cédula", a.advisor.cedula || "—"],
  ["Ciudad", a.advisor.city || "—"],
  ["Margen comercial", `${a.margin}%`],
  ["Compró al admin (unidades)", a.compradoUnits],
  ["Compró al admin (COP)", a.compradoValue],
  ["En consignación (unidades)", a.consignedUnits],
  ["En consignación (COP)", a.consignedValue],
  ["Total abonado (COP)", a.paid],
  ["Saldo que debe (COP)", a.debt],
];

const movementRows = (a: AdvisorAccount) =>
  a.movements.map((m) => ({
    Fecha: shortDate(m.date),
    Tipo: KIND_LABEL[m.kind],
    Producto: m.productName || "—",
    Cantidad: m.qty,
    "Valor unitario": m.unitValue,
    Valor: m.amount,
    Nota: m.note || "",
  }));

// Exporta UN asesor: hoja de resumen + hoja de movimientos.
export const exportAdvisorAccount = async (account: AdvisorAccount): Promise<void> => {
  const XLSX = await import("xlsx");
  const wb = XLSX.utils.book_new();

  const summary = XLSX.utils.aoa_to_sheet([
    ["Cuenta corriente del asesor"],
    ["Indicador", "Valor"],
    ...summaryRows(account),
  ]);
  summary["!cols"] = [{ wch: 34 }, { wch: 24 }];
  XLSX.utils.book_append_sheet(wb, summary, "Resumen");

  const rows = movementRows(account);
  const detail = XLSX.utils.json_to_sheet(
    rows.length
      ? rows
      : [
          {
            Fecha: "",
            Tipo: "",
            Producto: "",
            Cantidad: "",
            "Valor unitario": "",
            Valor: "",
            Nota: "",
          },
        ]
  );
  detail["!cols"] = [
    { wch: 14 },
    { wch: 20 },
    { wch: 30 },
    { wch: 10 },
    { wch: 14 },
    { wch: 14 },
    { wch: 26 },
  ];
  XLSX.utils.book_append_sheet(wb, detail, "Movimientos");

  XLSX.writeFile(wb, `cuenta-corriente-${slugify(advisorName(account)) || "asesor"}.xlsx`);
};

// Exporta TODA la red: una hoja resumen con todos los asesores.
export const exportAllAdvisorAccounts = async (accounts: AdvisorAccount[]): Promise<void> => {
  const XLSX = await import("xlsx");
  const wb = XLSX.utils.book_new();

  const rows = accounts.map((a) => ({
    Asesor: advisorName(a),
    Ciudad: a.advisor.city || "—",
    "Margen %": a.margin,
    "Compró und": a.compradoUnits,
    "Compró COP": a.compradoValue,
    "Consignado und": a.consignedUnits,
    "Consignado COP": a.consignedValue,
    "Abonado COP": a.paid,
    "Debe COP": a.debt,
  }));

  const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Asesor: "" }]);
  ws["!cols"] = [
    { wch: 26 },
    { wch: 14 },
    { wch: 10 },
    { wch: 11 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, "Resumen general");

  XLSX.writeFile(
    wb,
    `cuentas-corrientes-asesores-${new Date().toISOString().slice(0, 10)}.xlsx`
  );
};